<template>
    <footer class="footer">
        <div class="container-fluid d-flex flex-wrap justify-content-between align-items-center">
            <nav>
                <ul class="footer-menu" data-tour="footer.help">
                    <li>
                        <a href="#" @click.prevent="showHotkeyDiscoveryModal">
                            <i class="fa fa-keyboard"></i>
                            Keyboard Shortcuts
                        </a>
                    </li>
                    <li v-if="showWelcomeLink">
                        <a href="#" @click.prevent="showWelcomeModal">
                            <i class="fa fa-hand-sparkles"></i>
                            Welcome
                        </a>
                    </li>
                    <li v-if="showTourLink">
                        <a href="#" @click.prevent="openTourHub">
                            <i class="fa fa-route"></i>
                            Guided Tour
                        </a>
                    </li>
                </ul>
            </nav>
            <div class="copyright text-right">
                &copy; {{ year }}, CP Hub LLC. <br />
                <small v-b-tooltip.hover :title="versionTexts.tooltip">{{ versionTexts.display }}</small>
            </div>
        </div>

        <HotkeyDiscoveryModal ref="hotkeyDiscoveryModal" modal-id="hotkey-discovery-modal" />
        <WelcomeModal v-if="helpLinks" ref="welcomeModal" modal-id="welcome-modal"
            :sandbox-mode="isSandboxMode" :tour-disabled="tourDisabled" @start-tour="startWalkthrough" />
    </footer>
</template>
<script lang="ts">
import { Component, Mixins, Prop } from 'vue-property-decorator';
import HotkeyDiscoveryModal from '@/components/Modals/HotkeyDiscoveryModal.vue';
import WelcomeModal from '@/components/Welcome/WelcomeModal.vue';
import ClientServiceWorkerMixin from '@/mixins/ClientServiceWorkerMixin';
import { stampedVersion } from '@/util/updateCheck';
import { isTenantConfigLoaded, tenantFlag } from '@/services/tenantConfigService';
import { markWelcomeShown } from '@/services/welcomeService';
import { isGuidedTourDisabled, isWelcomeDisabled } from '@/services/onboardingRollout';

@Component({
    components: {
        HotkeyDiscoveryModal,
        WelcomeModal
    }
})
export default class ContentFooter extends Mixins(ClientServiceWorkerMixin) {
    /**
     * Set by DashboardLayout only. The Flex embed renders this same footer through the bare
     * Content layout and leaves it off, which is what keeps the help links out of the embed.
     *
     * `type: Boolean` is load-bearing, not decoration. tsconfig enables experimentalDecorators
     * without emitDecoratorMetadata, so vue-property-decorator cannot read the TypeScript type
     * and a bare `@Prop({ default: false })` reaches Vue with no declared type. Vue only casts a
     * valueless attribute to true for a Boolean-typed prop, so `<content-footer help-links />`
     * would otherwise arrive as the empty string and read as false.
     */
    @Prop({ type: Boolean, default: false }) helpLinks!: boolean;

    get year() { return new Date().getFullYear(); }
    // Populated from the shared worker only while the local client service is running;
    // a non-null value adds the "Client Service" segment to the footer version line.
    private clientServiceVersion: string | null = null;
    private hubConnected = false;
    private versionRetryCount = 0;

    // The UI build's git sha; null (unstamped local build) hides the segment.
    private readonly uiVersion: string | null = stampedVersion;

    // Displayed version (commit shortened) and tooltip (full commit hash) built together
    // so the shared reactive dependencies are evaluated once per update.
    // The API environment and version are read straight from the root rather than copied in by a
    // watcher: the DataHub often delivers them before this footer is created (a second tab, or a
    // footer remounted by a layout change), and a watcher never sees a value that is already there.
    private get versionTexts(): { display: string; tooltip: string } {
        const { environment, version } = this.$root.$data.dataHubFileVersion || {};
        const build = (apiVersion: string | null, uiVersion: string | null): string => {
            const api = [environment, apiVersion].filter(Boolean).join(' ');
            return [api, uiVersion && `UI ${uiVersion}`, this.clientServiceVersion && `Client Service ${this.clientServiceVersion}`]
                .filter(Boolean).join(' · ');
        };
        return {
            display: build(this.shortCommitVersion(version), this.uiVersion && this.uiVersion.slice(0, 7)),
            tooltip: build(version, this.uiVersion),
        };
    }

    // The API version is an InformationalVersion like "1.0.0-local+<40-char git sha>".
    // Shorten the build-metadata commit (everything after "+") to a 7-char short hash.
    private shortCommitVersion(version: string | null): string | null {
        if (!version) return version;
        const plusIndex = version.indexOf('+');
        if (plusIndex === -1) return version;
        return `${version.slice(0, plusIndex)}+${version.slice(plusIndex + 1, plusIndex + 8)}`;
    }

    created() {
        document.addEventListener("keyup", this.hotKeyListener);
        // The gate lives in DashboardLayout, which has no reliable ref to this footer's modal
        // (the footer is behind a v-if), so it asks over the root bus instead.
        this.$root.$on("welcome:auto-show", this.onAutoShowWelcome);
    }

    get tenantConfigLoaded(): boolean { return isTenantConfigLoaded(); }
    get isSandboxMode(): boolean { return tenantFlag("sandboxMode"); }
    get tourDisabled(): boolean { return isGuidedTourDisabled(); }

    /**
     * Hidden until the configuration fetch lands, so a tenant that has switched the modal off
     * never sees the link flash, and a failed fetch leaves it hidden rather than showing it.
     * Zero permissions mirrors the existing no-permissions banner state.
     */
    get showWelcomeLink(): boolean {
        return this.helpLinks
            && this.tenantConfigLoaded
            && !isWelcomeDisabled()
            && this.$permissions.length > 0;
    }

    /** The same conditions, against the tour's own kill switch. The two are independent. */
    get showTourLink(): boolean {
        return this.helpLinks
            && this.tenantConfigLoaded
            && !this.tourDisabled
            && this.$permissions.length > 0;
    }

    /**
     * Both tour entry points ask DashboardLayout to do the work: it owns the run, because it owns
     * the sidebar and top nav a tour points at, and it is the only component certain to outlive
     * one. It is also where the lazy chunk is loaded, so there is a single import site.
     */
    protected openTourHub() {
        this.$root.$emit("tour:open-hub");
    }

    /** The welcome modal's tour button, which has already finished hiding by now. */
    protected startWalkthrough() {
        this.$root.$emit("tour:start-walkthrough");
    }

    protected onClientServiceWorkerReady(_port: MessagePort): void {
        this.sendToWorker("clientServiceRefreshVersion");
    }

    protected onClientServiceWorkerMessage(e: MessageEvent): void {
        const [messageType, data] = e.data;
        if (messageType === "clientServiceVersion") {
            // null/empty => client service not running => line is hidden.
            this.clientServiceVersion = data || null;
            // If the hub is up but GetVersion returned null, retry up to 3 times
            // with a short delay so transient startup failures self-heal.
            if (!this.clientServiceVersion && this.hubConnected && this.versionRetryCount < 3) {
                this.versionRetryCount++;
                setTimeout(() => this.sendToWorker("clientServiceRefreshVersion"), 2000);
            }
        } else if (messageType === "clientServiceHubState") {
            if (data === "Connected") {
                this.hubConnected = true;
                this.versionRetryCount = 0;
                // Service came online — re-request the version.
                this.sendToWorker("clientServiceRefreshVersion");
            } else {
                // Closed or Reconnecting — clear the version display.
                this.hubConnected = false;
                this.clientServiceVersion = null;
            }
        }
    }

    beforeDestroy() {
        document.removeEventListener("keyup", this.hotKeyListener);
        this.$root.$off("welcome:auto-show", this.onAutoShowWelcome);
    }

    hotKeyListener(e: any) {
        if (e.key === '?' || (e.shiftKey && e.key === '/')) {
            if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
            // A stray `?` during a tour would open this modal over the tour overlay.
            if (this.$root.$data.tourActive) return;

            e.preventDefault();
            this.showHotkeyDiscoveryModal();
        }
    }

    showHotkeyDiscoveryModal() {
        const modal = this.$refs.hotkeyDiscoveryModal as HotkeyDiscoveryModal;
        modal?.show();
    }

    /** The footer link. Deliberately does not write the once-per-day guard. */
    showWelcomeModal() {
        (this.$refs.welcomeModal as any)?.show();
    }

    /** The layout's gate fired. Record the auto-show first, so a reload today stays quiet. */
    protected onAutoShowWelcome() {
        const modal = this.$refs.welcomeModal as any;
        if (!modal) return;
        markWelcomeShown(this.$user?.userName);
        modal.show();
    }
};
</script>
